import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loadStoredData } from './store/slices/authSlice';
import { fetchCurrentUser } from './store/actions/authActions';
import Landing from './pages/Landing';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import OTPVerification from './pages/OTPVerification';
import ResetPassword from './pages/ResetPassword';
import GoogleCallback from './pages/GoogleCallback';
import Dashboard from './pages/Dashboard';
import CreateScript from './pages/CreateScript';
import MyScripts from './pages/MyScripts';
import Settings from './pages/Settings';
import ResearchResults from './pages/ResearchResults';
import AIOutline from './pages/AIOutline';
import ScriptDetails from './pages/ScriptDetails';
import TopicLibrary from './pages/TopicLibrary';
import TopicDetails from './pages/TopicDetails';
import Script from './pages/Script';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Import styles
import './styles/global.css';
import { ToastProvider } from './components/ToastProvider';
import ToastInitializer from './components/ToastInitializer';

const ProtectedRoute = ({ children }) =>
{
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const token = useSelector((state) => state.auth.token);
  const tokenFromStorage = localStorage.getItem('token');

  if (!isAuthenticated && !token && !tokenFromStorage)
  {
    return <Navigate to="/signin" replace />;
  }
  return children;
};


const PublicRoute = ({ children }) =>
{
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const token = useSelector((state) => state.auth.token);
  const tokenFromStorage = localStorage.getItem('token');

  if (isAuthenticated || token || tokenFromStorage)
  {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};


const AuthInitializer = () =>
{
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);

  useEffect(() =>
  {
    const initializeAuth = async () =>
    {
      try
      {
        const authDataStr = localStorage.getItem('auth');
        const userDataStr = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        let authData = null;
        let userData = null;

        if (authDataStr)
        {
          try
          {
            authData = JSON.parse(authDataStr);
          } catch (e)
          {
            console.error('Failed to parse auth data:', e);
            localStorage.removeItem('auth');
          }
        }

        if (userDataStr)
        {
          try
          {
            userData = JSON.parse(userDataStr);
          } catch (e)
          {
            console.error('Failed to parse user data:', e);
            localStorage.removeItem('user');
          }
        }

        if (authData || userData)
        {
          dispatch(loadStoredData({ authData, userData }));
        }

        if (storedToken && !userData)
        {
          dispatch(fetchCurrentUser());
        }
      } catch (error)
      {
        console.error('Error loading stored auth data:', error);
      }
    };

    initializeAuth();
  }, [dispatch]);

  useEffect(() =>
  {
    if (token && !user)
    {
      dispatch(fetchCurrentUser());
    }
  }, [token, user, dispatch]);

  return null;
};

const RouteScrollHandler = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  
  return null;
};

function App()
{
  return (
    <ToastProvider>
      <ToastInitializer>
        <Router>
          <RouteScrollHandler />
          <AuthInitializer />
          <div className="App">
            <Routes>
              <Route
                path="/signin"
                element={
                  <PublicRoute>
                    <SignIn />
                  </PublicRoute>
                }
              />

              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignUp />
                  </PublicRoute>
                }
              />

              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />

              <Route
                path="/otp-verification"
                element={
                  <PublicRoute>
                    <OTPVerification />
                  </PublicRoute>
                }
              />

              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                }
              />

              <Route
                path="/google/callback"
                element={ <GoogleCallback /> }
              />

              <Route path="/privacy" element={ <PrivacyPolicy /> } />
              <Route path="/terms" element={ <TermsOfService /> } />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* Create Script */ }
              <Route
                path="/create-script"
                element={
                  <ProtectedRoute>
                    <CreateScript />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/topic-library"
                element={
                  <ProtectedRoute>
                    <TopicLibrary />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/research-results"
                element={
                  <ProtectedRoute>
                    <ResearchResults />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ai-outline"
                element={
                  <ProtectedRoute>
                    <AIOutline />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/script/:id"
                element={
                  <ProtectedRoute>
                    <Script />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/script"
                element={
                  <ProtectedRoute>
                    <Script />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/script-details"
                element={
                  <ProtectedRoute>
                    <ScriptDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/topic-details"
                element={
                  <ProtectedRoute>
                    <TopicDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/my-scripts"
                element={
                  <ProtectedRoute>
                    <MyScripts />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />

              {/* DEFAULT & ERROR ROUTES */ }

              {/* Default Route - Landing Page */ }
              <Route path="/" element={ <Landing /> } />

              {/* 404 Route - Redirect to Landing */ }
              <Route path="*" element={ <Navigate to="/" replace /> } />
            </Routes>
          </div>
        </Router>
      </ToastInitializer>
    </ToastProvider>
  );
}

export default App;